// Omnix/client/ui/NewConversationModal.js
import { ComponentBase } from "../core/ComponentBase.js";
import { createAvatarElement } from "../../helpers/avatarHelper.js";

export class NewConversationModal extends ComponentBase {
    static styles = `
        .new-conversation-modal .modal-dialog { max-width: 500px; }
        .new-conversation-modal .modal-body { min-height: 400px; max-height: 70vh; display: flex; flex-direction: column; }
        .search-results-container { flex-grow: 1; overflow-y: auto; margin-top: 1rem; }
        .contact-list-item { display: flex; align-items: center; gap: 0.75rem; }
        .contact-info { overflow: hidden; }
        .contact-name { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .contact-subtext { font-size: 0.8rem; color: var(--bs-secondary-color); }
        .channel-selection-view { text-align: center; }
        .channel-selection-list .list-group-item { display: flex; align-items: center; gap: 1rem; font-size: 1.1rem; }
        .channel-selection-list .list-group-item i { font-size: 1.5rem; color: var(--bs-primary); }
         .channel-selection-view {
            text-align: center;
            display: flex; /* Make this a flex container */
            flex-direction: column; /* Stack items vertically */
            height: 100%; /* Take full height of the modal body */
            overflow: hidden; /* Prevent the view itself from overflowing */
        }
        .channel-selection-list {
            flex-grow: 1; /* Allow the list to take up remaining space */
            overflow-y: auto; /* Add a scrollbar when needed */
            margin-top: 1rem; /* Keep the margin */
        }
    `;

    constructor(config) {
        super(config);
        this.modalInstance = null;
        this.searchResults = [];
        this.selectedContact = null;
        this._debouncedSearch = this.debounce(this.searchContacts.bind(this), 300);
        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'modal fade new-conversation-modal';
        this.container.id = `${this.config.instanceId}-modal`;
        this.container.setAttribute('tabindex', '-1');
        this.container.innerHTML = `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Start New Conversation</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body" id="${this.config.instanceId}-body"></div>
                </div>
            </div>
        `;
        this.modalInstance = new bootstrap.Modal(this.container);
        this.modalBody = this.container.querySelector(`#${this.config.instanceId}-body`);

        this.container.addEventListener('hidden.bs.modal', () => this.reset());
        document.body.appendChild(this.container);
        await super.initialize();
    }

    show() {
        this.reset();
        this.renderSearch();
        this.modalInstance.show();
    }

    reset() {
        this.searchResults = [];
        this.selectedContact = null;
    }

    renderSearch() {
        this.modalBody.innerHTML = `
            <input type="search" class="form-control" placeholder="Search for a contact by name, email, or company...">
            <div class="search-results-container list-group list-group-flush">
                <div class="text-muted text-center p-4">Start typing to find a contact.</div>
            </div>
        `;
        const searchInput = this.modalBody.querySelector('input');
        searchInput.addEventListener('input', (e) => this._debouncedSearch(e.target.value));
    }

    async searchContacts(term) {
        const resultsContainer = this.modalBody.querySelector('.search-results-container');
        if (term.length < 2) {
            resultsContainer.innerHTML = `<div class="text-muted text-center p-4">Start typing to find a contact.</div>`;
            return;
        }
        try {
            const result = await this.appClient.rpcClient.call('OmniContactsRpc.getData', {
                filter: {
                    $or: [
                        { firstName: { $regex: term, $options: 'i' } },
                        { lastName: { $regex: term, $options: 'i' } },
                        { email: { $regex: term, $options: 'i' } },
                        { company: { $regex: term, $options: 'i' } }
                    ]
                },
                pageSize: 20
            });
            this.searchResults = result.data || [];
            this.renderResults();
        } catch (error) {
            this.log.error("Error searching contacts:", error);
            resultsContainer.innerHTML = `<div class="text-danger text-center p-4">Error searching contacts.</div>`;
        }
    }

    renderResults() {
        const resultsContainer = this.modalBody.querySelector('.search-results-container');
        resultsContainer.innerHTML = '';
        if (this.searchResults.length === 0) {
            resultsContainer.innerHTML = `<div class="text-muted text-center p-4">No contacts found.</div>`;
            return;
        }

        this.searchResults.forEach(contact => {
            const item = document.createElement('a');
            item.href = '#';
            item.className = 'list-group-item list-group-item-action contact-list-item';

            const displayName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.email;
            const avatar = createAvatarElement({ displayName, sizeClass: 'default' });

            item.innerHTML = `
                ${avatar.outerHTML}
                <div class="contact-info">
                    <div class="contact-name">${displayName}</div>
                    <div class="contact-subtext">${contact.email || contact.company || ''}</div>
                </div>
            `;
            item.addEventListener('click', (e) => {
                e.preventDefault();
                this.selectedContact = contact;
                this.renderChannelSelection();
            });
            resultsContainer.appendChild(item);
        });
    }

    renderChannelSelection() {
        const displayName = `${this.selectedContact.firstName || ''} ${this.selectedContact.lastName || ''}`.trim() || this.selectedContact.email;
        const avatar = createAvatarElement({ displayName, sizeClass: 'medium' });

        const channelIcons = { email: 'bi-envelope-fill', sms: 'bi-chat-dots-fill', whatsapp: 'bi-whatsapp', voice: 'bi-telephone-fill', web_chat: 'bi-globe' };

        this.modalBody.innerHTML = `
            <div class="channel-selection-view">
                <button class="btn btn-link mb-3" id="back-to-search-btn"><i class="bi bi-arrow-left"></i> Back to search</button>
                ${avatar.outerHTML}
                <h5 class="mt-2">${displayName}</h5>
                <p class="text-muted">Select a channel to start the conversation.</p>
                <div class="list-group channel-selection-list mt-4">
                    ${this.selectedContact.channels.map(channel => `
                        <a href="#" class="list-group-item list-group-item-action" data-channel-type="${channel.type}" data-channel-value="${channel.value}">
                            <i class="bi ${channelIcons[channel.type] || 'bi-question-circle'}"></i>
                            <div class="text-start">
                                <strong>${this.t(channel.type)}</strong><br>
                                <small>${channel.value}</small>
                            </div>
                        </a>
                    `).join('')}
                </div>
            </div>
        `;

        this.modalBody.querySelector('#back-to-search-btn').addEventListener('click', () => this.renderSearch());

        this.modalBody.querySelectorAll('.channel-selection-list a').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                this.emit('startConversation', {
                    contact: this.selectedContact,
                    channel: {
                        type: link.dataset.channelType,
                        value: link.dataset.channelValue
                    }
                });
                this.modalInstance.hide();
            });
        });

    }

    debounce(func, delay) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), delay);
        };
    }

    render() {
        return this.container;
    }
}